import TextFieldSearch from 'components/TextFieldSearch/TextFieldSearch';
import { CaretDown } from '@phosphor-icons/react';
import { ENUM_REGEX } from 'constants';
import { Grid, MenuItem, TextField } from '@attain-sre/attain-design-system';
import { InputLabel, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import MultiSelect from 'components/MultiSelect';
import {
  handleDecimalBeforeInput,
  handleDecimalInput,
  handleDecimalKeyDown,
} from '../../helper/helper';
import * as Styled from './index.styles';

/**
 * DynamicForm Component
 * Renders a dynamic form based on the provided configuration.
 *
 * @param {Array} config - Array of control objects that define the form fields.
 * @param {Function} onChange - Callback function to handle changes in the form fields.
 */
const DynamicForm: React.FC<DynamicFormProps> = ({ config = [], onChange }) => {
  /**
   * Handles the change event for form fields.
   * @param {string} id - The unique identifier of the form field.
   * @param {string} value - The updated value of the form field.
   */
  const handleChange = (id: string, value: string | null | string[]) => {
    onChange(id, value);
  };

  /**
   * Handles paste events for textbox fields with regex validation.
   * @param {ClipboardEvent} e - The paste event.
   * @param {string} id - The unique identifier of the form field.
   * @param {string} regex - The regex pattern for validation.
   * @param {string} currentValue - The current value of the field.
   */
  const handleTextboxPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    id: string,
    regex: string,
    currentValue: string
  ) => {
    if (regex) {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');

      try {
        const regexObj = new RegExp(regex);

        // Always validate and paste or clear field
        if (regexObj.test(pastedText)) {
          // Paste text matches regex - paste it
          handleChange(id, pastedText);
        } else {
          // Paste text doesn't match regex - clear field
          handleChange(id, '');
        }
      } catch (err) {
        console.error('Invalid regex pattern:', regex, err);
        handleChange(id, '');
      }
    }
  };

  /**
   * Renders the appropriate control based on the control type.
   * @param {Object} control - The control object defining the form field.
   * @returns {JSX.Element | null} The rendered form control or null if the type is not supported.
   */
  const renderFormControl = (control: FormControl): JSX.Element | null => {
    const {
      type,
      id,
      label,
      options = [],
      value,
      rows = 4,
      placeholder = '',
      disabled = false,
      regex = '',
      required = false,
      isAllowPaste = false,
      ...otherProps
    } = control;

    switch (type) {
      case 'label':
        return (
          <Styled.DividerLabel key={id} style={control.style}>
            {control.htmlContent ? (
              <span dangerouslySetInnerHTML={{ __html: control.htmlContent }} />
            ) : (
              label
            )}
          </Styled.DividerLabel>
        );

      case 'textbox':
        return (
          <TextField
            key={id}
            id={id}
            label={label}
            value={value || ''}
            onBeforeInput={(e) => {
              if (
                [ENUM_REGEX.DECIMAL, ENUM_REGEX.WHOLE_NUMBER].includes(regex)
              ) {
                handleDecimalBeforeInput(e, regex);
              }
            }}
            onKeyDown={(e) => {
              if (regex && [ENUM_REGEX.DECIMAL, ENUM_REGEX.WHOLE_NUMBER].includes(regex)) {
                handleDecimalKeyDown(e, value || '', regex);
              }
            }}
            onInput={(e) => {
              if (
                [ENUM_REGEX.DECIMAL, ENUM_REGEX.WHOLE_NUMBER].includes(regex)
              ) {
                handleDecimalInput(e, value || '', (newValue) => handleChange(id, newValue), regex);
              }
            }}
            onPaste={(e) => {
              if (isAllowPaste) {
                handleTextboxPaste(e, id, regex, value || '');
              }
            }}
            onChange={(e) => handleChange(id, e.target.value)}
            fullWidth
            placeholder
            required={required}
            disabled={disabled}
            error={control.error || false}
            {...otherProps}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={id}
            id={id}
            label={label}
            value={value || ''}
            onChange={(e) => handleChange(id, e.target.value)}
            multiline
            rows={rows}
            fullWidth
            disabled={disabled}
            {...otherProps}
          />
        );

      case 'autocomplete':
        return (
          <Styled.SelectAutoCompleteGroup
            key={id}
            id={id}
            value={value || null}
            onChange={(e, newValue) => handleChange(id, newValue)}
            displayEmpty
            popupIcon={<CaretDown />}
            fullWidth
            options={options}
            disabled={disabled}
            style={{ marginTop: 30 }}
            renderInput={(params) => (
              <TextFieldSearch
                {...params}
                placeholder={placeholder + (required ? ' *' : '')}
                variant="outlined"
              />
            )}
            renderOption={(props, option) => (
              <Styled.SelectAutoCompleteGroupOptions>
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              </Styled.SelectAutoCompleteGroupOptions>
            )}
            {...otherProps}
          />
        );

      case 'dropdown':
        return (
          <Styled.StyledFormControl
            key={id}
            fullWidth
            variant="outlined"
            required={required}
            sx={control.style}
          >
            <InputLabel id={`${id}-label`} shrink>
              {label}
            </InputLabel>
            <Styled.StyledSelect
              labelId={`${id}-label`}
              id={id}
              value={value || ''}
              onChange={(e) => handleChange(id, e.target.value)}
              disabled={disabled}
              label={label}
              sx={{ height: 42 }}
              {...otherProps}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Styled.StyledSelect>
          </Styled.StyledFormControl>
        );

      case 'multiselect': {
        const multiselectValue = Array.isArray(value) ? value : [];
        // Handle both {id, label} and {value, label} option formats
        // For multiselect, we use id if available, otherwise use value as string
        const multiselectOptions = options?.map(opt => {
          // Check if option has 'id' property (for multiselect config) or 'value' property (for standard config)
          const optionId = opt.id || opt.value?.toString() || '';
          return {
            id: optionId,
            label: opt.label || '',
          };
        }).filter(opt => opt.id !== '') || [];
        
        return (
          <Styled.StyledFormControl
            key={id}
            fullWidth
            variant="outlined"
            required={required}
            sx={control.style}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              marginBottom: '-2px',
              marginTop: '18px',
              marginLeft: '12px',
              fontSize: '14px',
              position: 'relative'
            }}>
              <InputLabel 
                id={`${id}-label`} 
                shrink 
                required={required}
                style={{ 
                  marginBottom: 0,
                  position: 'static',
                  transform: 'none',
                  fontSize: '14px',
                  fontFamily: 'Calibre-Regular, sans-serif'
                }}
              >
                {label}
              </InputLabel>
              {control.tooltip && (
                <Tooltip title={control.tooltip} arrow placement="top">
                  <InfoIcon style={{ 
                    fontSize: '16px', 
                    color: '#666', 
                    cursor: 'help',
                    flexShrink: 0,
                    marginLeft: '2px'
                  }} />
                </Tooltip>
              )}
            </div>
            <MultiSelect
              options={multiselectOptions}
              value={multiselectValue}
              onChange={(selectedValues) => handleChange(id, selectedValues)}
              placeholder=''
              disabled={disabled}
            />
          </Styled.StyledFormControl>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Grid container>
      {config.map((control) => (
        <Grid item key={control.id} {...control.gridProps}>
          {renderFormControl(control)}
        </Grid>
      ))}
    </Grid>
  );
};

interface FormControlOption {
  id?: string;
  label: string;
  value: string | number;
}

// Define type for individual form control
interface FormControl {
  id: string;
  type: 'label' | 'textbox' | 'textarea' | 'autocomplete' | 'dropdown' | 'multiselect'; // Supported control types
  label?: string;
  value?: string | null | string[]; // For multiselect, value is string[]
  rows?: number; // Number of rows (for textarea)
  placeholder?: string;
  options?: FormControlOption[];
  disabled?: boolean;
  regex?: string;
  isAllowPaste?: boolean; // Allow pasting functionality
  helperText?: string; // Helper text that can indicate cleanup rules
  tooltip?: string; // Tooltip text for info icon
  gridProps?: Record<string, any>; // Props for the Grid container
  [key: string]: any; // Allow additional props
}

// Props type for DynamicForm component
interface DynamicFormProps {
  config: FormControl[]; // Array of form controls
  onChange: (id: string, value: string | null | string[]) => void; // Callback for handling changes
}

export default DynamicForm;
